
/**************************************************************************
 * Created on Thu Nov 28 2024
 *
 *  # Honeywell Code Usage Policy
 *
 * **Honeywell Source Code Usage Policy Classification:** Highly Confidential
 *
 * [comment]: # (Options for above are "Inner Source", "General Application", "Highly Confidential", "Export Controlled")
 *
 * **This repository has multiple classifications:** Yes
 *
 * [comment]: # (Options for above are "Yes" when subfolders contain a separate policy,"No" when the whole repo has the same policy)
 *
 * **Honeywell confidential - For internal use only.
 *
 * Property of and copyright ownership by Honeywell International Inc. and/or its affiliates.
 *
 * Use of source code must follow all Honeywell policies including the [HCE Source Code Access Policy](http: //go.honeywell.com/hce-code-access-policy).
 *
 * External use or distribution prohibited without written approval per Honeywell policies**
 *
********************************************************************************/

import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { MENUPOSITION } from './modules/dashboard/model';
import { IUserDetail } from './auth-provider/models/customer-model';


interface IDSSContext
{
    t: (key: string) => string,
    translate: (key: string) => string,
    country: ICountry[],
    menuPosition: MENUPOSITION,
    selectedCustomerInfo?: any,
    pageHeading: string,
    setPageHeading: React.Dispatch<React.SetStateAction<string>>,
    setMenuPosition: React.Dispatch<React.SetStateAction<MENUPOSITION>>,
    className: string,
    setClassName: React.Dispatch<React.SetStateAction<string>>,
    userDetail: IUserDetail | null,
    setUserDetail: React.Dispatch<React.SetStateAction<IUserDetail | null>>,
    selectedCustomer: string,
    setSelectedCustomer: React.Dispatch<React.SetStateAction<string>>,
    active: string,
    setActive: React.Dispatch<React.SetStateAction<string>>,
    guestTokenData: any,
    setGuestTokenData: React.Dispatch<React.SetStateAction<any>>,
    allowedRoutes: string[],
    setAllowedRoutes: React.Dispatch<React.SetStateAction<string[]>>,
    allowedRoutesReady: boolean,
};


interface IDSSProvider
{
    children: React.ReactNode;
}
interface ICountry
{
    label: string, value: string
}

const DSSContext = createContext<IDSSContext>({} as IDSSContext);

const DSSProvider = ({ children }: IDSSProvider) =>
{

    const [ translation, setTranslation ] = useState<{ [ key: string ]: string }>({});
    const [ country, setCountry ] = useState<ICountry[]>([]);
    const [ pageHeading, setPageHeading ] = useState<string>('');
    const [ className, setClassName ] = useState<string>('');
    const [ userDetail, setUserDetail ] = useState<IUserDetail | null>(null);
    const [ selectedCustomer, setSelectedCustomer ] = useState(() => sessionStorage.getItem('selectedCustomer') || '');
    const [ menuPosition, setMenuPosition ] = useState<MENUPOSITION>(MENUPOSITION.HORIZONTAL);
    const [active, setActive] = useState(' active');
    const [guestTokenData, setGuestTokenData] = useState<any>(null);
    const [allowedRoutes, setAllowedRoutes] = useState<string[]>([]);
    const [allowedRoutesReady, setAllowedRoutesReady] = useState<boolean>(false);

    const setAllowedRoutesWithReady: React.Dispatch<React.SetStateAction<string[]>> = (value) => {
        const resolved = typeof value === 'function' ? value(allowedRoutes) : value;
        setAllowedRoutes(resolved);
        // Only mark as ready when routes are actually populated (not when cleared)
        if (resolved.length > 0) {
            setAllowedRoutesReady(true);
        }
    };
    const langUrl = '/lang/en/translation.json';
    const broadcastPromo = '/lang/en/broadcat-promotion.json';
    const packslip='/lang/en/packslip.json';
    const countryUrl = '/country.json';


    useEffect(() =>
    {
        Promise.all([
            fetch(langUrl).then(res => res.json()).catch(() => ({})),
            fetch(broadcastPromo).then(res => res.json()).catch(() => ({})),
            fetch(packslip).then(res => res.json()).catch(() => ({}))
        ]).then(([mainTrans, broadcastpromoTrans, packslipTrans]) => {
            setTranslation({ ...mainTrans, ...broadcastpromoTrans, ...packslipTrans });
        }).catch(e => console.log(e));
 
        fetch(countryUrl)
            .then(response => response.json())
            .then(data => setCountry(data))
            .catch(e => console.log(e));

    }, []);

    useEffect(() => {
        if (selectedCustomer) {
            sessionStorage.setItem('selectedCustomer', selectedCustomer);
            return;
        }
        sessionStorage.removeItem('selectedCustomer');
    }, [selectedCustomer]);


    // const t = (key: string) => translation[ key ] || key;
    const getNestedTranslation = (obj: any, key: string): string | undefined => {
         return key.split('.').reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), obj);
    };

    const t = (key: string) => {
        const value = getNestedTranslation(translation, key);
        return value !== undefined ? value : key;
    };
    return (

        <DSSContext.Provider value={ useMemo(() =>
        {
            const translate = t;

            return {
                translate, t, country,
                pageHeading, setPageHeading,
                menuPosition, setMenuPosition,
                className, setClassName,
                userDetail, setUserDetail,
                selectedCustomer, setSelectedCustomer,
                active, setActive,
                guestTokenData, setGuestTokenData,
                allowedRoutes, setAllowedRoutes: setAllowedRoutesWithReady,
                allowedRoutesReady
            };
        }, [ t, country,
            pageHeading, menuPosition, className,
            userDetail, selectedCustomer, guestTokenData, allowedRoutes, allowedRoutesReady ]) }>
            { children }
        </DSSContext.Provider>

    );
};

const useDssContext = () => useContext(DSSContext);

export { DSSProvider, useDssContext };
